import React, { FC, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled, { ThemeProvider } from "styled-components";
import { AppContainer } from "@abb/abb-common-ux-react/components/AppContainer";
import NormalizedStyles from "../../styles/normalize";
import { commonUXTheme } from "../../styles/commonUXVariables";
import NotificationViewer from "./NotificationViewer";
import { userActions } from "../../applications/common/actions/userActions";
import {
  getNotifications,
  getConfirmEvents,
  getShouldFetchUserInformation,
  getAccessToken,
  getUser,
  getAuthSettings,
  getAuthInformation
} from "../../applications/common/reducers/userReducer";
import { userSagas } from "../../applications/common/sagas/userSagas";
import ConfirmDialog from "./ConfirmDialog";
import { usePrevious } from "../hooks/usePrevious";
import { CssStyles } from "../../styles/index";
import { deliveriesHomeViewActions } from "applications/deliveries/deliveriesHome/actions/deliveriesHomeViewActions";
import { useHistory, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { LanguageProvider } from "applications/common/components/LanguageProvider";
import { MenuBar } from "shared/menubar/containers/MenuBar";
import { PrivacyConsentDialog } from "./PrivacyConsentDialog";
import { commonActions } from "applications/common/actions/commonActions";
import { applicationNotificationViewActions } from "applications/applicationNotifications/actions/applicationNotitficationActions";
import {
  ApplicationNotificationOperations,
  ApplicationNotificationTypes,
  CompanyDto,
  NonTransientNotification,
  RoleGroups,
  TransientNotification,
  NotificationSeverity
} from "api";
import { routes } from "utilities/routes";
import { getApplicationRoleGroup } from "applications/common/reducers/commonReducer";
import { useMsal } from "@azure/msal-react";
import { loginRequestScopes } from "configuration/authConfig";
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
  StyledEngineProvider
} from "@mui/material/styles";
import { productInformationActions } from "applications/deliveries/productInformation/actions/productInformationActions";
import { ordersViewActions } from "applications/deliveries/orders/actions/ordersViewActions";
import {
  defaultOrderCriteria,
  orderSearchFormDefaultFields
} from "applications/deliveries/orders/reducers/ordersViewReducer";

const ApplicationContainer = styled(AppContainer)`
  background: ${(props) => props.theme.colors.whitePrimary};
`;

export const App: FC = (props) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(getUser);
  const authInformation = useSelector(getAuthInformation);
  const authSettings = useSelector(getAuthSettings);
  const notifications = useSelector(getNotifications);
  const confirmEvents = useSelector(getConfirmEvents);
  const applicationRoleGroup = useSelector(getApplicationRoleGroup);
  const shouldFetchUserInformation = useSelector(getShouldFetchUserInformation);
  const prevShouldFetchUserInformation = usePrevious(shouldFetchUserInformation);
  const accessToken = useSelector(getAccessToken);
  const location = useLocation();
  const history = useHistory();
  const basePath = process.env.REACT_APP_BASEPATH;

  const { instance, accounts, inProgress } = useMsal();

  useEffect(() => {
    if (accounts.length > 0) {
      dispatch(userActions.storeUserInfo(accounts[0]));
    } else {
      dispatch(userActions.changeIsLoggingIn(false));
    }
  }, [accounts, inProgress, dispatch]);

  useEffect(() => {
    if (currentUser.accountInfo && authSettings && currentUser.shouldFetchToken) {
      console.log("Fetching token");
      dispatch(
        userSagas.getToken.createAction({
          request: {
            scopes: [authSettings.resourceScope, "offline_access"],
            account: currentUser.accountInfo,
            forceRefresh: false
          },
          authSettings: authSettings
        })
      );
    }
  }, [currentUser, authSettings, dispatch]);

  useEffect(() => {
    if (shouldFetchUserInformation && prevShouldFetchUserInformation === false) {
      dispatch(userSagas.getUserInformation.createAction(undefined));
    }
  }, [shouldFetchUserInformation, prevShouldFetchUserInformation, dispatch]);

  const handleRemoveConfirmEvent = (id: string) => {
    dispatch(userActions.removeConfirmEvent(id));
  };

  // Used to handle external users from Test Maintenance. If the user is not logged in, but has gone through the initial checking phase
  // we are triggering the onSignIn-functionality to prevent users from having to click the "sign in"-button.
  // at this phase, the user should have a session open as the user is navigating from TM...
  useEffect(() => {
    if (location.pathname === routes.deliveries.productInformation.index) {
      if (authInformation.initialUserCheckDone && !authInformation.isSignedIn) {
        instance.loginRedirect();
      }
    }
    // eslint-disable-next-line
  }, [location, authInformation]);

  useEffect(() => {
    let groupToSet: RoleGroups | undefined = undefined;
    if (location.pathname.startsWith(routes.deliveries.home)) {
      groupToSet = RoleGroups.DeliverIt;
    } else if (
      location.pathname.startsWith(routes.quickPriceCheck) ||
      location.pathname.startsWith(routes.shoppingCart)
    ) {
      groupToSet = RoleGroups.Configurator;
    } else {
      groupToSet = undefined;
    }
    if (applicationRoleGroup !== groupToSet) {
      dispatch(commonActions.setApplicationRoleGroup(groupToSet));
    }
  }, [location, dispatch, applicationRoleGroup]);

  // "translation" is the default namespace where the translations are loaded from
  // useSuspense is false, because this component doesn't have any Suspenses as its parents
  const { t, ready } = useTranslation("translation", { useSuspense: false });

  const handleTokenRefresh = () => {
    if (authSettings && currentUser && currentUser.accountInfo) {
      dispatch(
        userSagas.getToken.createAction({
          request: {
            scopes: [authSettings.resourceScope, "offline_access"],
            account: currentUser.accountInfo,
            forceRefresh: false
          },
          authSettings: authSettings,
          redirectToLogInPageWhenRefreshFails: true
        })
      );
    }
  };

  // SignalR handling
  const handleNonTransientNotification = useCallback(
    (message: NonTransientNotification) => {
      dispatch(userActions.addNotification(NotificationSeverity.Success, message.header));
      switch (message.type) {
        case ApplicationNotificationTypes.UserAccessRightsChanged:
          dispatch(userSagas.getUserInformation.createAction(undefined));
          break;
      }
    },
    [dispatch]
  );

  const handleTransientNotification = useCallback(
    (message: TransientNotification) => {
      dispatch(
        userActions.addNotification(
          message.severity as NotificationSeverity,
          message.header,
          message.isConfirmation,
          10000
        )
      );
    },
    [dispatch]
  );

  const handleOnConfiguratorBaseUrlSet = useCallback(
    (baseUrl?: string) => {
      dispatch(commonActions.setConfiguratorBaseUrl(baseUrl));
    },
    [dispatch]
  );

  const handleOnNotificationStateChanged = useCallback(
    (
      unreadNotificationCount: number,
      operation?: ApplicationNotificationOperations,
      notificationIds?: number[]
    ) => {
      dispatch(
        applicationNotificationViewActions.setNotificationState(
          unreadNotificationCount,
          operation,
          notificationIds
        )
      );
    },
    [dispatch]
  );

  const handleClickNavigationItem = useCallback(
    (internalRoute: string) => {
      if (internalRoute === routes.deliveries.productInformation.index) {
        dispatch(productInformationActions.setProductInformationSearchCriteria(undefined));
      } else if (internalRoute === routes.deliveries.orders.index) {
        dispatch(ordersViewActions.changeOrderCriteria(defaultOrderCriteria));
        dispatch(ordersViewActions.changeSearchFields(orderSearchFormDefaultFields));
      }

      history.push(internalRoute);
    },
    [history, dispatch]
  );

  const getPixelAmount = (cssString: string) => {
    return Number(cssString.split("px")[0]);
  };

  const theme = createTheme({
    breakpoints: {
      values: {
        xs: getPixelAmount(commonUXTheme.breakpoints.xs),
        sm: getPixelAmount(commonUXTheme.breakpoints.sm),
        md: getPixelAmount(commonUXTheme.breakpoints.md),
        lg: getPixelAmount(commonUXTheme.breakpoints.lg),
        xl: getPixelAmount(commonUXTheme.breakpoints.xl),
        xxl: getPixelAmount(commonUXTheme.breakpoints.xxl)
      }
    }
  });

  return (
    <ThemeProvider theme={commonUXTheme}>
      <MuiThemeProvider theme={theme}>
        <StyledEngineProvider injectFirst>
          <LanguageProvider>
            <ApplicationContainer>
              <NotificationViewer
                notifications={notifications}
                onRemoveNotification={(id: string) => {
                  dispatch(userActions.removeNotification(id));
                }}
                translateText={ready ? t : undefined}
              />
              <ConfirmDialog
                confirmEvents={confirmEvents}
                onRemoveConfirmItem={handleRemoveConfirmEvent}
              />
              <NormalizedStyles />
              <CssStyles />
              <PrivacyConsentDialog />
              <MenuBar
                accessToken={accessToken}
                basePath={basePath ?? ""}
                isSignedIn={accessToken !== undefined && accessToken.length > 0}
                activePath={location.pathname}
                onClickMenuItem={handleClickNavigationItem}
                onSignIn={() => {
                  instance.loginRedirect({
                    scopes: loginRequestScopes,
                    prompt: "select_account"
                  });
                }}
                onSignOut={() => {
                  instance.logoutRedirect();
                }}
                onSelectCompany={(
                  company: CompanyDto | undefined,
                  companyHasApprover: boolean | undefined
                ) => {
                  dispatch(
                    deliveriesHomeViewActions.changeSelectedCompany(company, !!companyHasApprover)
                  );
                }}
                useExternalLinks={false}
                translateText={ready ? t : undefined}
                onConfiguratorBaseUrlSet={handleOnConfiguratorBaseUrlSet}
                onRefreshToken={handleTokenRefresh}
                debug={process.env.NODE_ENV === "development"}
                onNonTransientNotificationReceived={handleNonTransientNotification}
                onTransientNotificationReceived={handleTransientNotification}
                onNotificationStateChanged={handleOnNotificationStateChanged}
                roleGroup={applicationRoleGroup}
              />
              {props.children}
            </ApplicationContainer>
          </LanguageProvider>
        </StyledEngineProvider>
      </MuiThemeProvider>
    </ThemeProvider>
  );
};
